/**
 * Laboratorio: catálogo de exámenes, órdenes y resultados.
 *
 * La orden nace en la consulta, la cobra caja y la procesa el laboratorio.
 * Sin pagar no se procesa: la misma regla que en la atención.
 */
import { apiFetch } from "./client";

export type MetodoPago = "EFECTIVO" | "YAPE" | "PLIN" | "TRANSFERENCIA" | "TARJETA";

export interface Examen {
  id: number;
  codigo: string;
  nombre: string;
  area: string | null;
  tipo_muestra: string | null;
  preparacion: string | null;
  horas_resultado: number | null;
  unidad: string | null;
  valor_referencia: string | null;
  /** Ocupa equipo y horario: admisión debe darle cita. */
  requiere_cita: boolean;
  servicio_id: number | null;
  precio: string | number;
  activo: boolean;
}

export interface OrdenItem {
  id: number;
  examen_id: number;
  descripcion: string;
  precio: string | number;
  valor: string | null;
  texto: string | null;
  unidad: string | null;
  valor_referencia: string | null;
  fuera_de_rango: boolean;
  resultado_en: string | null;
  tiene_resultado: boolean;
  procesado_por_nombre: string | null;
  tipo_muestra: string | null;
  preparacion: string | null;
}

export type EstadoOrden =
  | "SOLICITADA"
  | "EN_PROCESO"
  | "CON_RESULTADOS"
  | "ENTREGADA"
  | "ANULADA";

export interface OrdenLab {
  id: number;
  episodio_id: number;
  paciente_id: number;
  paciente_nombre: string;
  profesional_id: number;
  profesional_nombre: string | null;
  sede_id: number;
  sede_nombre: string | null;
  cita_id: number | null;
  fecha: string;
  estado: EstadoOrden;
  diagnostico_presuntivo: string | null;
  observacion: string | null;
  urgente: boolean;
  total: string | number;
  pagada: boolean;
  metodo_pago: string | null;
  cobrada_por_nombre: string | null;
  cobrada_en: string | null;
  items: OrdenItem[];
  resultados_cargados: number;
  /** Alguno necesita cita: lo agenda admisión. */
  requiere_agenda: boolean;
}

export interface OrdenCreate {
  episodio_id: number;
  /** Añadir a una atención ya firmada. El backend responde 409 sin esto. */
  confirmar_posterior?: boolean;
  examen_ids: number[];
  diagnostico_presuntivo?: string | null;
  observacion?: string | null;
  urgente?: boolean;
}

export interface ResultadoIn {
  valor?: string | null;
  texto?: string | null;
  unidad?: string | null;
  valor_referencia?: string | null;
  fuera_de_rango?: boolean;
}

const qs = (p: Record<string, string | number | boolean | undefined>) => {
  const u = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  });
  const s = u.toString();
  return s ? `?${s}` : "";
};

export function listExamenes(params: { q?: string } = {}): Promise<Examen[]> {
  return apiFetch<Examen[]>(`/v1/laboratorio/examenes${qs(params)}`);
}

export function crearExamen(payload: Partial<Examen>): Promise<Examen> {
  return apiFetch<Examen>("/v1/laboratorio/examenes", { method: "POST", body: payload });
}

export function actualizarExamen(id: number, payload: Partial<Examen>): Promise<Examen> {
  return apiFetch<Examen>(`/v1/laboratorio/examenes/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function crearOrden(payload: OrdenCreate): Promise<OrdenLab> {
  return apiFetch<OrdenLab>("/v1/laboratorio/ordenes", { method: "POST", body: payload });
}

export function listOrdenes(
  params: {
    paciente_id?: number;
    episodio_id?: number;
    estado?: string;
    por_cobrar?: boolean;
    por_procesar?: boolean;
  } = {},
): Promise<OrdenLab[]> {
  return apiFetch<OrdenLab[]>(`/v1/laboratorio/ordenes${qs(params)}`);
}

export function getOrden(id: number): Promise<OrdenLab> {
  return apiFetch<OrdenLab>(`/v1/laboratorio/ordenes/${id}`);
}

export function cobrarOrden(id: number, metodo_pago: MetodoPago): Promise<OrdenLab> {
  return apiFetch<OrdenLab>(`/v1/laboratorio/ordenes/${id}/cobrar`, {
    method: "POST",
    body: { metodo_pago },
  });
}

/**
 * La constancia del pago de un examen.
 *
 * Va en dos pasos —cobrar y luego el archivo—, igual que en una cita: si la
 * subida falla, el cobro ya quedó hecho y el paciente no espera por un
 * problema de red. `apiFetch` fuerza JSON, así que aquí va fetch crudo.
 */
export async function subirConstanciaOrden(
  ordenId: number,
  file: File,
): Promise<OrdenLab> {
  const { getTokens } = await import("../auth/tokens");
  const tokens = getTokens();
  const formData = new FormData();
  formData.append("file", file);
  const base = (import.meta.env.VITE_API_BASE_URL ?? "/api") as string;
  const response = await fetch(`${base}/v1/laboratorio/ordenes/${ordenId}/constancia`, {
    method: "POST",
    headers: tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {},
    body: formData,
  });
  if (!response.ok) throw new Error("No se pudo subir la constancia.");
  return response.json();
}

export function tomarMuestra(id: number): Promise<OrdenLab> {
  return apiFetch<OrdenLab>(`/v1/laboratorio/ordenes/${id}/tomar-muestra`, {
    method: "POST",
  });
}

export function registrarResultado(
  ordenId: number,
  itemId: number,
  payload: ResultadoIn,
): Promise<OrdenLab> {
  return apiFetch<OrdenLab>(
    `/v1/laboratorio/ordenes/${ordenId}/items/${itemId}/resultado`,
    { method: "PUT", body: payload },
  );
}

export function entregarOrden(id: number): Promise<OrdenLab> {
  return apiFetch<OrdenLab>(`/v1/laboratorio/ordenes/${id}/entregar`, { method: "POST" });
}

export function anularOrden(id: number, motivo: string): Promise<OrdenLab> {
  return apiFetch<OrdenLab>(`/v1/laboratorio/ordenes/${id}/anular`, {
    method: "POST",
    body: { motivo },
  });
}
